import { Component, inject, Input, NgZone } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subject, take } from 'rxjs';

function flattenControls(form: AbstractControl): AbstractControl[] {
    let extracted: AbstractControl[] = [form];
    if (form instanceof FormArray || form instanceof FormGroup) {
        const children = Object.values(form.controls).map(flattenControls);
        extracted = extracted.concat(...children);
    }
    return extracted;
}

export type PathObservableFunction = (path: string) => Observable<any> | null;
export type PathObservableFunctionList = (path: string[]) => Observable<any[]>;
export type PathObservableFunctionMap = (path: string[]) => Observable<Record<string, any>>;

export type PathFunctionsMap = {
    getObservableValue: PathObservableFunction;
    getObservableValueList: PathObservableFunctionList;
    getObservableValueMap: PathObservableFunctionMap;
};

@Component({
    template: '',
    standalone: false,
})
export abstract class SeBaseStepContentComponent {
    protected readonly zone = inject(NgZone);
    @Input() readonly = false;
    title: string = '';
    name: string = '';
    _form: FormGroup | null = null;

    get form(): FormGroup | null {
        return this._form;
    }

    form$: BehaviorSubject<FormGroup | null> = new BehaviorSubject<FormGroup | null>(null);
    isReady$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /*isValid$ potrebbe non servire si gestisce tutto tramite il form*/
    isValid$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    isMandatory$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    errorsList$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    /*per evitare errori sul template. dato che i form interni ad ogni step vengono riempiti dopo l'afterinit
     * serve un ngIf su questo flag per evitare errori in console*/
    formIsFilled: boolean = false;

    getObservableValueFunctions$: BehaviorSubject<null | PathFunctionsMap> =
        new BehaviorSubject<null | PathFunctionsMap>(null);
    getObservableValue: null | PathObservableFunction = null;
    getObservableValueList: null | PathObservableFunctionList = null;
    getObservableValueMap: null | PathObservableFunctionMap = null;

    /*usato per leggere il valore iniziale di un controllo*/
    getControlValue: null | ((path: string) => any) = null;
    getControlValueList: null | ((paths: string[]) => any[]) = null;

    firstChangeTriggered: boolean = false;

    gotoNextStep$: Subject<string> | null = null;

    currentActiveStep$: BehaviorSubject<string> | null = null;

    constructor() {
        combineLatest([this.form$, this.getObservableValueFunctions$]).subscribe(([form, pathFunctions]) => {
            if (!pathFunctions) return;
            const { getObservableValue, getObservableValueList, getObservableValueMap } = pathFunctions;
            this.getObservableValue = getObservableValue;
            this.getObservableValueMap = getObservableValueMap;
            this.getObservableValueList = getObservableValueList;

            this._form = form;
            this.zone.onStable.pipe(take(1)).subscribe(() => {
                this.isReady$.next(Boolean(pathFunctions && form));
                setTimeout(() => {
                    /*chiamo fillForm astratta qui ma implementata in tutte le classi derivate
                     * ciascun sotto componente cosi inizializza il suo form con le proprie logiche*/
                    if (typeof this.fillForm === 'function') {
                        this.fillForm();
                    }

                    this.applyReadonlyState();
                    /*appena il form e` generato controllo subito la presenza di required e setto optional*/
                    this.parseControlsForOptional();
                }, 1);
            });
        });
    }

    protected abstract fillForm(): void;

    protected parseControlsForOptional = () => {
        if (!this.form) return;
        const flattenedControls = flattenControls(this.form);
        const isMandatory = flattenedControls.filter(Boolean).some((control) => {
            return control.hasValidator(Validators.required);
        });
        this.isMandatory$.next(isMandatory);
    };

    /**
     * verifica se tutti gli input del form di questo step sono validi in tal caso emette true
     * che viene poi gestito nel <se-step> per segnalare che lo step e` completed
     * se si volesse comportamento diverso si puo sovrascrivere questo metodo
     * oppure non chiamarlo e usare direttamente l'osservabile this.isValid$()
     */
    protected parseControlsForValidity = () => {
        if (!this.form) return;
        const flattenedControls = flattenControls(this.form);
        const excludeDisabledControls = flattenedControls.filter((control) => !control.disabled);
        const isValid = excludeDisabledControls.filter(Boolean).every((control) => {
            return control.valid;
        });
        this.isValid$.next(isValid);
    };

    protected parseControlsForErrors = () => {
        if (!this.form) return;
        const flattenedControls = flattenControls(this.form);
        const errors: string[] = flattenedControls
            .filter(Boolean)
            .map((control) => (control.errors ? Object.keys(control.errors) : []))
            .filter((errors) => errors.length > 0)
            .flat();

        this.errorsList$.next(errors);
    };

    protected canSkipValueChanges = (prev: any): boolean => {
        if (!this.firstChangeTriggered && Object.keys(prev).every((k) => !prev[k] && prev[k] === this.form?.value[k]))
            return true;
        else {
            this.firstChangeTriggered = true;
            return false;
        }
    };

    protected markAndUpdateFormControl = (path: string) => {
        const control = this.form?.get(path.split('.'));
        if (!control) return;
        control.markAllAsTouched();
        control.updateValueAndValidity();
    };

    /**
     * tramite il Subject gotoNextStep$ appartenente ad se-stepper fa una richiesta
     * al componente contenitore di spostarsi sul prossimo step (senza chiamare direttamente un suo metodo)
     * se non fosse possibile passare allo step successivo non accadrebbe nulla
     * @function gotoNextStep
     * @returns {void}
     */
    protected gotoNextStep = (): void => {
        this.gotoNextStep$?.next(this.name);
    };

    private applyReadonlyState() {
        if (!this.form) return;

        if (this.readonly) {
            this.form.disable({ emitEvent: false });
            return;
        }
    }
}
